/**
 * ImageDocumentViewer
 *
 * Image viewer component with bounding box overlay support.
 * Captures image dimensions and renders citation highlights.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';

import type { Citation, PageDimensions } from '../types';
import { BoundingBoxOverlay } from './BoundingBoxOverlay';
import { useDocumentComparison } from '../hooks/useDocumentComparison';

interface ImageDocumentViewerProps {
  /** Column ID for state management */
  columnId: string;
  /** URL to the image */
  url: string;
  /** All citations for this document (all on page 1) */
  citations: Citation[];
  /** Map from citation to field path */
  citationFieldMap: Map<Citation, string>;
}

export function ImageDocumentViewer({
  columnId,
  url,
  citations,
  citationFieldMap,
}: ImageDocumentViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Local state
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number>(500);
  const [imageDimensions, setImageDimensions] = useState<PageDimensions | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Context
  const {
    hoveredFieldPath,
    lockedFieldPath,
    setHoveredField,
    setHoveredCitation,
    setLockedField,
    setPageDimensions,
  } = useDocumentComparison();

  // Measure container width for responsive sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width - 32;
        setContainerWidth(Math.max(200, width));
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Image load handler
  const handleImageLoad = useCallback(() => {
    const img = imageRef.current;
    if (!img) return;

    const dimensions: PageDimensions = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    };

    setImageDimensions(dimensions);
    setPageDimensions(columnId, 1, dimensions);
    setIsLoading(false);
    setError(null);
  }, [columnId, setPageDimensions]);

  const handleImageError = useCallback(() => {
    const errorMsg = 'Failed to load image';
    Sentry.captureMessage(errorMsg, { extra: { url } });
    setError(errorMsg);
    setIsLoading(false);
  }, [url]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  // Citation interaction handlers
  const handleCitationHover = useCallback(
    (citation: Citation | null, fieldPath: string | null) => {
      setHoveredCitation(citation);
      if (fieldPath) {
        setHoveredField(fieldPath);
      }
    },
    [setHoveredCitation, setHoveredField],
  );

  const handleCitationClick = useCallback(
    (_citation: Citation, fieldPath: string) => {
      setLockedField(fieldPath);
    },
    [setLockedField],
  );

  // Calculate scaled dimensions
  const scaledWidth = containerWidth * scale;
  const aspectRatio = imageDimensions
    ? imageDimensions.height / imageDimensions.width
    : 1;
  const scaledHeight = scaledWidth * aspectRatio;

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {/* Controls Header */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 p-2 border-b bg-muted/30">
        {/* Page indicator (images are single page) */}
        <span className="text-sm text-muted-foreground">Image</span>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Image Content */}
      <div className="flex-1 overflow-auto p-4">
        {error ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <p className="text-destructive mb-2">Error loading image</p>
            <p className="text-sm text-muted-foreground">
              Please try downloading the file instead.
            </p>
          </div>
        ) : (
          <div
            className="relative inline-block"
            style={{
              width: scaledWidth,
              height: isLoading ? 400 : scaledHeight,
            }}
          >
            {isLoading && (
              <div className="absolute inset-0 flex justify-center items-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            <img
              ref={imageRef}
              src={url}
              alt="Document"
              onLoad={handleImageLoad}
              onError={handleImageError}
              className="max-w-none"
              style={{
                width: scaledWidth,
                height: 'auto',
                display: isLoading ? 'none' : 'block',
              }}
            />

            {/* Bounding Box Overlay */}
            {imageDimensions && !isLoading && (
              <BoundingBoxOverlay
                citations={citations}
                pageDimensions={imageDimensions}
                currentPage={1}
                hoveredFieldPath={hoveredFieldPath}
                lockedFieldPath={lockedFieldPath}
                citationFieldMap={citationFieldMap}
                onCitationHover={handleCitationHover}
                onCitationClick={handleCitationClick}
                scale={scale}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
